// Pure helpers for the invoice tool: money, dates, numbering, totals, words.

export const CURRENCIES = {
  GBP: { symbol: "£", pdf: "£", name: "Pounds", sub: "Pence", label: "British Pound" },
  INR: { symbol: "₹", pdf: "Rs. ", name: "Rupees", sub: "Paise", label: "Indian Rupee" },
  USD: { symbol: "$", pdf: "$", name: "Dollars", sub: "Cents", label: "US Dollar" },
  EUR: { symbol: "€", pdf: "€", name: "Euros", sub: "Cents", label: "Euro" },
  AUD: { symbol: "A$", pdf: "A$", name: "Australian Dollars", sub: "Cents", label: "Australian Dollar" },
  CAD: { symbol: "C$", pdf: "C$", name: "Canadian Dollars", sub: "Cents", label: "Canadian Dollar" },
  AED: { symbol: "AED ", pdf: "AED ", name: "Dirhams", sub: "Fils", label: "UAE Dirham" },
  SGD: { symbol: "S$", pdf: "S$", name: "Singapore Dollars", sub: "Cents", label: "Singapore Dollar" },
  NZD: { symbol: "NZ$", pdf: "NZ$", name: "New Zealand Dollars", sub: "Cents", label: "New Zealand Dollar" },
  CHF: { symbol: "CHF ", pdf: "CHF ", name: "Swiss Francs", sub: "Rappen", label: "Swiss Franc" },
  JPY: { symbol: "¥", pdf: "JPY ", name: "Yen", sub: "Sen", label: "Japanese Yen" },
  CNY: { symbol: "CN¥", pdf: "CNY ", name: "Yuan", sub: "Fen", label: "Chinese Yuan" },
  HKD: { symbol: "HK$", pdf: "HK$", name: "Hong Kong Dollars", sub: "Cents", label: "Hong Kong Dollar" },
  ZAR: { symbol: "R ", pdf: "R ", name: "Rand", sub: "Cents", label: "South African Rand" },
  SAR: { symbol: "SAR ", pdf: "SAR ", name: "Riyals", sub: "Halalas", label: "Saudi Riyal" },
  QAR: { symbol: "QAR ", pdf: "QAR ", name: "Riyals", sub: "Dirhams", label: "Qatari Riyal" },
  KWD: { symbol: "KWD ", pdf: "KWD ", name: "Dinars", sub: "Fils", label: "Kuwaiti Dinar" },
  BHD: { symbol: "BHD ", pdf: "BHD ", name: "Dinars", sub: "Fils", label: "Bahraini Dinar" },
  OMR: { symbol: "OMR ", pdf: "OMR ", name: "Rials", sub: "Baisa", label: "Omani Rial" },
  MYR: { symbol: "RM ", pdf: "RM ", name: "Ringgit", sub: "Sen", label: "Malaysian Ringgit" },
  THB: { symbol: "฿", pdf: "THB ", name: "Baht", sub: "Satang", label: "Thai Baht" },
  IDR: { symbol: "Rp ", pdf: "Rp ", name: "Rupiah", sub: "Sen", label: "Indonesian Rupiah" },
  PHP: { symbol: "₱", pdf: "PHP ", name: "Pesos", sub: "Centavos", label: "Philippine Peso" },
  NPR: { symbol: "NPR ", pdf: "NPR ", name: "Rupees", sub: "Paisa", label: "Nepalese Rupee" },
  LKR: { symbol: "LKR ", pdf: "LKR ", name: "Rupees", sub: "Cents", label: "Sri Lankan Rupee" },
  BDT: { symbol: "৳", pdf: "BDT ", name: "Taka", sub: "Poisha", label: "Bangladeshi Taka" },
  PKR: { symbol: "PKR ", pdf: "PKR ", name: "Rupees", sub: "Paisa", label: "Pakistani Rupee" },
  NGN: { symbol: "₦", pdf: "NGN ", name: "Naira", sub: "Kobo", label: "Nigerian Naira" },
  KES: { symbol: "KSh ", pdf: "KSh ", name: "Shillings", sub: "Cents", label: "Kenyan Shilling" },
  EGP: { symbol: "E£", pdf: "EGP ", name: "Pounds", sub: "Piastres", label: "Egyptian Pound" },
  BRL: { symbol: "R$", pdf: "R$", name: "Reais", sub: "Centavos", label: "Brazilian Real" },
  MXN: { symbol: "MX$", pdf: "MX$", name: "Pesos", sub: "Centavos", label: "Mexican Peso" },
  SEK: { symbol: "SEK ", pdf: "SEK ", name: "Kronor", sub: "Öre", label: "Swedish Krona" },
  NOK: { symbol: "NOK ", pdf: "NOK ", name: "Kroner", sub: "Øre", label: "Norwegian Krone" },
  DKK: { symbol: "DKK ", pdf: "DKK ", name: "Kroner", sub: "Øre", label: "Danish Krone" },
  PLN: { symbol: "zł ", pdf: "PLN ", name: "Zloty", sub: "Groszy", label: "Polish Zloty" },
  TRY: { symbol: "₺", pdf: "TRY ", name: "Lira", sub: "Kurus", label: "Turkish Lira" },
  KRW: { symbol: "₩", pdf: "KRW ", name: "Won", sub: "Jeon", label: "South Korean Won" },
};

export const TAX_MODES = {
  intra: { label: "GST – Intra-state (CGST + SGST)", short: "CGST + SGST" },
  inter: { label: "GST – Inter-state (IGST)", short: "IGST" },
  export: { label: "Export of services (LUT, zero-rated)", short: "Export (0%)" },
  vat: { label: "VAT / Sales tax", short: "VAT" },
  none: { label: "No tax", short: "No tax" },
};

export const DOC_TYPES = {
  invoice: { title: "TAX INVOICE", label: "Tax Invoice" },
  proforma: { title: "PROFORMA INVOICE", label: "Proforma Invoice" },
  quote: { title: "QUOTATION", label: "Quotation" },
  credit: { title: "CREDIT NOTE", label: "Credit Note" },
};

// Credit notes reduce what a client owes; everything else adds.
export const docSign = (inv) => ((inv.docType || "invoice") === "credit" ? -1 : 1);
export const isBillable = (inv) => ["invoice", "credit"].includes(inv.docType || "invoice");

// Two ready-made invoice formats. "export" reproduces the GBP invoice layout;
// "gst" is the domestic Indian GST invoice.
export const FORMATS = {
  export: { label: "International / Export", currency: "GBP", taxMode: "export" },
  gst: { label: "Domestic GST (India)", currency: "INR", taxMode: "intra" },
};

export const COPY_LABELS = [
  "ORIGINAL FOR RECIPIENT",
  "DUPLICATE FOR TRANSPORTER",
  "TRIPLICATE FOR SUPPLIER",
];

export const uid = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export function fmtMoney(n, currency = "GBP", { pdf = false } = {}) {
  const c = CURRENCIES[currency] || { symbol: currency + " ", pdf: currency + " " };
  const s = Math.abs(Number(n) || 0).toLocaleString(currency === "INR" ? "en-IN" : "en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (n < 0 ? "-" : "") + (pdf ? c.pdf : c.symbol) + s;
}

export const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export function addDays(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + Number(days || 0));
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export function fmtDate(iso, style = "iso") {
  if (!iso) return "N/A";
  if (style === "iso") return iso;
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function lastDayOfMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m, 0));
  return d.toISOString().slice(0, 10);
}

export function monthLabel(ym) {
  if (!/^\d{4}-\d{2}/.test(ym || "")) return "";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// Indian financial year (April – March), e.g. 2026-08-31 -> "2026-27".
export function financialYear(iso) {
  const d = iso ? new Date(iso + "T00:00:00") : new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
}

const DOC_SUFFIX = { proforma: "PI", quote: "QT", credit: "CN" };

export function prefixFor(settings, docType, brand) {
  const n = settings.numbering || {};
  const base = brand?.prefix || n.prefix || "INV";
  const suffix = DOC_SUFFIX[docType];
  return suffix ? `${base}${n.separator ?? "/"}${suffix}` : base;
}

export function counterKey(settings, docType, date, brand) {
  const fy = settings.numbering?.resetEachFY === false ? "all" : financialYear(date);
  return `${prefixFor(settings, docType, brand)}|${fy}`;
}

export function formatNumber(settings, docType, date, seq, brand) {
  const n = settings.numbering || {};
  const parts = [prefixFor(settings, docType, brand)];
  if (n.includeFY !== false) parts.push(financialYear(date));
  parts.push(String(seq).padStart(Number(n.padding) || 0, "0"));
  return parts.join(n.separator ?? "/");
}

export function suggestNumber(settings, invoices, docType, date, brand) {
  const key = counterKey(settings, docType, date, brand);
  let seq = Number(settings.numbering?.counters?.[key]) || Number(settings.numbering?.start) || 1;
  const taken = new Set(invoices.map((i) => i.number));
  while (taken.has(formatNumber(settings, docType, date, seq, brand))) seq++;
  return { number: formatNumber(settings, docType, date, seq, brand), seq, key };
}

export function computeTotals(inv) {
  const mode = inv.taxMode || "none";
  const taxable = mode === "intra" || mode === "inter" || mode === "vat";
  // every document (preview, PDF, Excel) lists lines A–Z
  const lines = sortLines(inv.items || []).map((it) => {
    const qty = num(it.qty);
    const rate = num(it.rate);
    const amount = round2(qty * rate);
    const taxRate = taxable ? num(it.taxRate) : 0;
    const tax = round2((amount * taxRate) / 100);
    return { ...it, qty, rate, amount, taxRate, tax };
  });
  const subtotal = round2(lines.reduce((s, l) => s + l.amount, 0));
  const taxTotal = round2(lines.reduce((s, l) => s + l.tax, 0));
  const cgst = mode === "intra" ? round2(taxTotal / 2) : 0;
  const sgst = mode === "intra" ? round2(taxTotal - cgst) : 0;
  const igst = mode === "inter" ? taxTotal : 0;
  const vat = mode === "vat" ? taxTotal : 0;
  const discount = round2(num(inv.discount));
  const credit = round2(num(inv.credit));
  const roundOff = round2(num(inv.roundOff));
  const total = round2(subtotal + taxTotal - discount - credit + roundOff);
  const pays = inv.payments || [];
  const received = round2(pays.filter((p) => !p.creditNoteId).reduce((s, p) => s + num(p.amount), 0));
  const credited = round2(pays.filter((p) => p.creditNoteId).reduce((s, p) => s + num(p.amount), 0));
  const tds = round2(pays.reduce((s, p) => s + num(p.tds), 0));
  const paid = round2(received + tds + credited);
  const balance = round2(total - paid);
  const rates = [...new Set(lines.map((l) => l.taxRate))];
  const rate = rates.length === 1 ? rates[0] : taxable ? null : 0;
  return { lines, subtotal, taxTotal, cgst, sgst, igst, vat, roundOff, discount, credit, total, paid, received, credited, tds, balance, rate };
}

export const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
  sent: { label: "Sent", cls: "bg-blue-100 text-blue-700" },
  partial: { label: "Partly paid", cls: "bg-amber-100 text-amber-800" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
  overdue: { label: "Overdue", cls: "bg-red-100 text-red-700" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-200 text-zinc-600 line-through" },
  issued: { label: "Issued", cls: "bg-violet-100 text-violet-700" },
  accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-700" },
  declined: { label: "Declined", cls: "bg-zinc-200 text-zinc-600" },
};

export function invoiceStatus(inv, totals = computeTotals(inv)) {
  if (inv.status === "cancelled") return "cancelled";
  const type = inv.docType || "invoice";
  if (type === "credit") return inv.status === "draft" ? "draft" : "issued";
  if (type === "quote" || type === "proforma") {
    return ["draft", "accepted", "declined"].includes(inv.status) ? inv.status : "sent";
  }
  if (totals.total > 0 && totals.balance <= 0 && totals.paid > 0) return "paid";
  if (inv.status === "paid") return "paid";
  if (totals.received > 0 || totals.tds > 0 || totals.credited > 0) return "partial";
  if (inv.status === "draft") return "draft";
  if (inv.dueDate && inv.dueDate < todayISO()) return "overdue";
  return "sent";
}

// ---------- amount in words ----------
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function below100(n) {
  if (n < 20) return ONES[n];
  return TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
}
function below1000(n) {
  const h = Math.floor(n / 100);
  const r = n % 100;
  return [h ? ONES[h] + " Hundred" : "", r ? below100(r) : ""].filter(Boolean).join(" ");
}

function intlWords(n) {
  if (n === 0) return "Zero";
  const scales = ["", "Thousand", "Million", "Billion", "Trillion"];
  const parts = [];
  let i = 0;
  while (n > 0) {
    const chunk = n % 1000;
    if (chunk) parts.unshift(below1000(chunk) + (scales[i] ? " " + scales[i] : ""));
    n = Math.floor(n / 1000);
    i++;
  }
  return parts.join(", ");
}

function indianWords(n) {
  if (n === 0) return "Zero";
  const parts = [];
  const crore = Math.floor(n / 1e7);
  n %= 1e7;
  const lakh = Math.floor(n / 1e5);
  n %= 1e5;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  if (crore) parts.push((crore >= 1000 ? indianWords(crore) : below1000(crore)) + " Crore");
  if (lakh) parts.push(below100(lakh) + " Lakh");
  if (thousand) parts.push(below100(thousand) + " Thousand");
  if (n) parts.push(below1000(n));
  return parts.join(" ");
}

export function amountInWords(amount, currency = "GBP") {
  const c = CURRENCIES[currency] || { name: currency, sub: "Cents" };
  const neg = amount < 0;
  const abs = Math.abs(round2(amount));
  const whole = Math.floor(abs);
  const fraction = Math.round((abs - whole) * 100);
  const toWords = currency === "INR" ? indianWords : intlWords;
  const minus = neg ? "Minus " : "";
  if (currency === "INR") {
    return `${minus}Rupees ${toWords(whole)}${fraction ? ` and ${below100(fraction)} Paise` : ""} Only.`;
  }
  return `${minus}${toWords(whole)} ${c.name}${fraction ? ` and ${below100(fraction)} ${c.sub}` : ""}.`;
}

// Parse pasted spreadsheet rows: "Description <tab> [qty] <tab> rate".
export function parsePastedLines(text) {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const cells = line.includes("\t") ? line.split("\t").map((c) => c.trim()) : null;
      if (cells && cells.length > 1) {
        const nums = cells.slice(1).map(num);
        const rate = nums[nums.length - 1];
        const qty = nums.length > 1 ? nums[0] || 1 : 1;
        return { description: cells[0], qty, rate };
      }
      const m = line.match(/^(.*?)[\s,;|]+[£$€₹]?\s*([\d,]+(?:\.\d+)?)$/);
      if (m) return { description: m[1].trim(), qty: 1, rate: num(m[2]) };
      return { description: line, qty: 1, rate: 0 };
    });
}

// Natural A–Z order ("Item 2" before "Item 10"), ignoring case and accents.
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
export const compareText = (a, b) => collator.compare(String(a ?? "").trim(), String(b ?? "").trim());

// Invoice lines in A–Z order by description; blank lines go last.
export function sortLines(items = []) {
  return [...items].sort((a, b) => {
    const A = String(a.description || "").trim();
    const B = String(b.description || "").trim();
    if (!A !== !B) return A ? -1 : 1;
    return compareText(A, B);
  });
}

export function projectLine(p) {
  return [p.name, p.service].filter(Boolean).join(" ");
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function safeFileName(s) {
  return String(s || "invoice").replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, "_");
}

export function toCSV(rows) {
  return rows
    .map((r) =>
      r.map((v) => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    )
    .join("\n");
}

export function daysBetween(a, b) {
  return Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 864e5);
}

// Days overdue buckets for the aging report.
export function agingBucket(inv, today = todayISO()) {
  const due = inv.dueDate || inv.date;
  const d = daysBetween(due, today);
  if (d <= 0) return "current";
  if (d <= 30) return "1-30";
  if (d <= 60) return "31-60";
  if (d <= 90) return "61-90";
  return "90+";
}

export function sanitizePhone(p) {
  return String(p || "").replace(/[^0-9]/g, "");
}
